import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyProvider, useCompany } from '../../context/CompanyContext';
import CompanyRegistrationCheck from '../CompanyRegistrationCheck';
import DashboardLayout from './DashboardLayout'; // Assuming DashboardLayout is the base layout
import ConfirmationDialog from '../ui/ConfirmationDialog';
import { getCompanyStatus, SimplifiedCompanyStatus } from '../../utils/authUtils';
import { useAuth } from '../../hooks/useAuth';

interface CompanyLayoutProps {
  children: ReactNode;
}

const CompanyLayout: FC<CompanyLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const { companyDetails: c, loadingState, jobPostCount } = useCompany();
  const navigate = useNavigate();
  const [companyStatus, setCompanyStatus] = useState<SimplifiedCompanyStatus['status'] | null>(null);
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [modalReason, setModalReason] = useState<string>('');

  useEffect(() => {
    if (user?.role === 'Company' && !loadingState.companyDetails) {
      const status: SimplifiedCompanyStatus = getCompanyStatus(c);
      setCompanyStatus(status.status);

      if (c && (c.verificationStatus === 'Blocked' || c.verificationStatus === 'Rejected')) {
        setShowStatusModal(true);
        setModalReason(c.verificationHistory && c.verificationHistory.length > 0
          ? c.verificationHistory[0].reason || 'No reason provided.'
          : 'No reason provided.');
      } else {
        setShowStatusModal(false);
        setModalReason('');
      }
    } else {
      setCompanyStatus(null);
      setShowStatusModal(false);
      setModalReason('');
    }
  }, [user, c, loadingState.companyDetails]);

  const handleModalConfirm = () => {
    navigate('/company/register');
  };

  return (
    <>
      <CompanyRegistrationCheck>
        <DashboardLayout companyDetails={{
          companyStatus,
          jobPostCount
        }} >
          <ConfirmationDialog
        isOpen={showStatusModal}
        onClose={() => {}} // Prevent closing
        onConfirm={handleModalConfirm}
        title={`${c?.verificationStatus} Status`}
        message=""
        confirmText="Re-register Company"
        variant={c?.verificationStatus === 'Rejected' ? 'danger' : 'warning'}
        showCloseButton={false} // Explicitly hide close button
        closeOnOverlayClick={false} // Prevent closing on overlay click
        showConfirmButton= {c?.verificationStatus != 'Blocked'}
      >
        <div className="text-left">
          <p className="text-sm text-gray-600 mb-2">
            Your company's registration has been{' '}
            <span className="font-semibold lowercase">{c?.verificationStatus}</span>.
          </p>
          {modalReason && (
            <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
              <p className="text-sm font-medium text-gray-800">Reason:</p>
              <p className="text-sm text-gray-700">
                {modalReason}
              </p>
            </div>
          )}
          <p className="text-sm text-gray-600">
            For further queries, please contact us:
          </p>
          <p className="text-sm text-gray-600">
            Email: <a href="mailto:operations@dhoondh.in" className="text-primary-lightGreen hover:underline">operations@dhoondh.in</a>
          </p>
          <p className="text-sm text-gray-600">
            Phone: <a href="tel:+919316846322" className="text-primary-lightGreen hover:underline">+91 93168 46322</a>
          </p>
        </div>
      </ConfirmationDialog>
        {children}
        </DashboardLayout>
      </CompanyRegistrationCheck>
    </>
  );
};

export default CompanyLayout;
